import { useCallback, useMemo, useSyncExternalStore } from 'react'
import type { Route } from '@metro/routing'
import type { LineDisplay } from '@metro/route-card'
import {
  buildPlatformBoundsByLeg,
  buildPossibleTransfersByLeg,
  platformBoundLookup,
  type StationResolver,
  type ConvenientCarType,
  type ConvenientCarsTransition,
} from '@metro/route-details'
import { useMetroMapStore } from './useMetroMapStore'
import { effectiveTheme } from './effectiveTheme'
import { useRoutingAdapter } from './useRoutingAdapter'
import { useSchema } from '../components/useSchema'
import { resolveStationById } from '../interaction/stationResolver'
import type { SchemaData } from '../schema/types'

/**
 * Schema line ids that render the striped/outlined connector in
 * RouteDetails — the SAME set the MAP draws dashed, and the
 * cross-platform canon exactly: DLR + Overground + Trams (Trams drawn
 * outlined on the map too — owner 2026-10-19).
 *
 * Parity: iOS `RouteDetailsLineBlockFactory._isOutlined`
 * (`.dlr` / `.overground` / `.tram`) and the Android host
 * `MetroShellViewModel.outlinedLines` (`dlr` / `overground` / `tram`).
 * Tube lines AND `NATIONAL_RAIL` render SOLID — National Rail is
 * deliberately excluded to match iOS/Android and the dashed map lines.
 * Keyed by `line.id`, the same shape as `leg.line_id`.
 */
export function outlinedLineIdsFromSchema(schema: SchemaData | null): ReadonlySet<string> {
  const set = new Set<string>()
  for (const station of schema?.stations ?? []) {
    const type = station.line.type
    if (type === 'DLR' || type === 'OVERGROUND' || type === 'TRAM') set.add(station.line.id)
  }
  return set
}

/**
 * Ride-leg indices (into `route.legs`) whose line is outlined — the
 * `outlinedLegs` contract consumed by `buildRouteDetailsSections`
 * (`outlinedLegs?.has(legIndex)`). Non-ride legs and legs without a
 * resolvable outlined line are excluded.
 */
export function rideLegOutlinedIndices(
  route: Route | null,
  outlinedLineIds: ReadonlySet<string>,
): ReadonlySet<number> {
  const set = new Set<number>()
  if (route === null) return set
  route.legs.forEach((leg, index) => {
    if (leg.type !== 'ride' || leg.line_id === undefined) return
    if (outlinedLineIds.has(leg.line_id)) set.add(index)
  })
  return set
}

/**
 * Everything `MobileRouteDetailsOverlay` needs as props, derived from
 * the MetroMap store + routing adapter + schema (TASK-412).
 *
 * Hoisted out of {@link MobileRouteCardOverlay} (which inlined this at
 * `:286-304`) so both the phone overlay and the desktop side panel
 * (`StationCardOrDetailsRouter`) build the identical payload — the
 * RouteDetails inputs are reused 1:1 across surfaces, not
 * re-implemented (spec §5). The web realises the cross-platform
 * `possibleTransfer` contract from the bundled schema's per-approach
 * convenient-car data; `isPremium` is always `false` on web (no paywall).
 */
export interface RouteDetailsPayload {
  /** All route variants (the chip row); empty outside the results state. */
  readonly rawRoutes: readonly Route[]
  /** The carousel-selected route, or `null` when there is none. */
  readonly selectedRoute: Route | null
  /** line_id → display info (from the routing adapter); `null` until loaded. */
  readonly lineLookup: ReadonlyMap<string, LineDisplay> | null
  /** Resolves a route stop's stationId → display name (host schema lookup). */
  readonly resolveStation: StationResolver
  /** Convenient-transfer cars per ride-leg index (drives S4/S5). */
  readonly possibleTransfersByLeg: Readonly<Record<number, readonly ConvenientCarType[]>>
  /** Platform sign ("Westbound", …) per ride-leg index, from the schema. */
  readonly platformBoundsByLeg: Readonly<Record<number, string>>
  /**
   * `true` when RouteDetails can be opened — results lifecycle, a
   * selected route, and the routing adapter has loaded.
   */
  readonly canOpen: boolean
  /**
   * Ride-leg indices (into `selectedRoute.legs`) whose line renders the
   * striped/outlined connector — the SAME set the map draws dashed: DLR +
   * Overground (cross-platform canon). Empty when there is no selected
   * route. Consumed by `buildRouteDetailsSections`'
   * `outlinedLegs?.has(legIndex)`.
   */
  readonly outlinedLegs: ReadonlySet<number>
}

export function useRouteDetailsPayload(): RouteDetailsPayload {
  const schema = useSchema()
  const adapter = useRoutingAdapter()
  const store = useMetroMapStore()
  const routeStatus = useSyncExternalStore(store.subscribe, () => store.getState().routeStatus)
  // The EFFECTIVE theme — the signal that picks the map's light/dark
  // MMTG geometry, night-tube included. Pills/rails must match the map
  // in system-dark AND night mode (2026-07 contract).
  const theme = useSyncExternalStore(store.subscribe, () => effectiveTheme(store.getState()))

  // Resolve a route stop's stationId → display name, line + coordinate
  // (schema-agnostic seam; null while the schema is still loading). The
  // coordinate feeds the DIRECT-WALK map (origin + destination) so it can
  // draw a street-following walking route; ride routes ignore it.
  const resolveStation: StationResolver = useCallback(
    (stationId) => {
      const resolved = resolveStationById(schema, stationId)
      if (resolved === null) return null
      const coordinate = resolved.station.coordinate
      return {
        localName: resolved.station.localName,
        lineId: resolved.station.line.id,
        ...(coordinate !== undefined ? { coordinate } : {}),
      }
    },
    [schema],
  )

  // transition id → its convenient-car data + endpoints + reversible
  // flag. Web's OFFLINE routes derive live S4/S5 cars from this bundled
  // schema source (contract-identical output to the iOS/Android wire
  // field). Memoised on the schema reference (stable post-fetch).
  const convenientCarsByTransition = useMemo(() => {
    const map = new Map<string, ConvenientCarsTransition>()
    for (const t of schema?.transitions ?? []) {
      if (t.convenientTransferCars === undefined) continue
      map.set(t.id, {
        fromStationId: t.fromStationId,
        toStationId: t.toStationId,
        reversible: t.reversible ?? false,
        cars: t.convenientTransferCars,
      })
    }
    return map
  }, [schema])

  const rawRoutes: readonly Route[] = routeStatus.kind === 'results' ? routeStatus.routes : []
  const selectedRoute: Route | null =
    routeStatus.kind === 'results'
      ? (routeStatus.routes[routeStatus.selectedIndex] ?? routeStatus.routes[0] ?? null)
      : null

  // Convenient cars for the selected route's interchanges (the web
  // realisation of the cross-platform `possibleTransfer` contract). A
  // cheap pure pass — memoised on the selected route + schema map.
  const possibleTransfersByLeg = useMemo(
    () =>
      selectedRoute === null
        ? {}
        : buildPossibleTransfersByLeg(selectedRoute, (id) => convenientCarsByTransition.get(id)),
    [selectedRoute, convenientCarsByTransition],
  )

  // (line, boarding, next station) → platform sign, indexed once per
  // schema; the per-route pass is one lookup per ride leg. Same source for
  // online and offline routes — both carry schema station ids.
  const boundLookup = useMemo(() => platformBoundLookup(schema?.connections ?? []), [schema])
  const platformBoundsByLeg = useMemo(
    () => (selectedRoute === null ? {} : buildPlatformBoundsByLeg(selectedRoute, boundLookup)),
    [selectedRoute, boundLookup],
  )

  const canOpen = routeStatus.kind === 'results' && selectedRoute !== null && adapter != null

  // Outlined line ids (DLR + Overground + Trams) — memoised on the schema ref
  // (stable post-fetch) so the station scan runs once, not per route.
  const outlinedLineIds = useMemo(() => outlinedLineIdsFromSchema(schema), [schema])

  // Ride-leg indices of the selected route whose line is outlined. A pure
  // pass over the selected route's legs — memoised on the route + id set.
  const outlinedLegs = useMemo(
    () => rideLegOutlinedIndices(selectedRoute, outlinedLineIds),
    [selectedRoute, outlinedLineIds],
  )

  return {
    rawRoutes,
    selectedRoute,
    lineLookup: adapter?.lineLookupFor(theme) ?? null,
    resolveStation,
    possibleTransfersByLeg,
    platformBoundsByLeg,
    canOpen,
    outlinedLegs,
  }
}
